import { Dialog } from '@headlessui/react';
import { AlertTriangle, CheckCircle, Info } from 'react-feather';
import { Button } from 'SubComponents/Buttons';

export enum DialogModalType {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

interface DialogModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  type?: DialogModalType;
  title: string;
  content: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const getDialogIcon = (type: DialogModalType) => {
  switch (type) {
    case DialogModalType.INFO:
      return <Info className="size-8 text-blue-600" />;
    case DialogModalType.WARNING:
      return <AlertTriangle className="size-8 text-yellow-600" />;
    case DialogModalType.ERROR:
      return <AlertTriangle className="size-8 text-red-600" />;
    case DialogModalType.SUCCESS:
      return <CheckCircle className="size-8 text-green-600" />;
    default:
      return <Info className="size-8 text-blue-600" />;
  }
};

const getDialogIconBgColor = (type: DialogModalType) => {
  switch (type) {
    case DialogModalType.INFO:
      return 'bg-blue-100';
    case DialogModalType.WARNING:
      return 'bg-yellow-100';
    case DialogModalType.ERROR:
      return 'bg-red-100';
    case DialogModalType.SUCCESS:
      return 'bg-green-100';
    default:
      return 'bg-blue-100';
  }
};

export default function DialogModal(props: DialogModalProps) {
  const DialogIcon = getDialogIcon(props.type || DialogModalType.INFO);
  const dialogIconBgColor = getDialogIconBgColor(props.type || DialogModalType.INFO);

  return (
    <Dialog open={props.open} onClose={props.setOpen} className="relative z-10">
      <Dialog.Backdrop className="fixed inset-0 bg-black/50 z-[1] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-6 text-center sm:p-0">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95">
            <div className="bg-white p-6 sm:p-10 sm:pb-6">
              <div className="flex gap-6 flex-col items-center sm:flex-row sm:items-start">
                <div
                  className={`mx-auto flex size-16 shrink-0 items-center justify-center rounded-full sm:mx-0 ${dialogIconBgColor}`}
                >
                  {DialogIcon}
                </div>
                <div className="text-center sm:text-left">
                  <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                    {props.title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{props.content}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 flex flex-col sm:flex-row sm:px-10 sm:py-6 gap-6 justify-center items-center">
              <Button
                data-autofocus
                onClick={props.onCancel}
                className="!text-[#44525f] !bg-white !border !border-solid !border-[#44525f] !w-full"
              >
                {props.cancelText}
              </Button>
              <Button onClick={props.onConfirm} className="!w-full">
                {props.confirmText}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
